import React, { useState, useEffect, useContext, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  Grid,
  TextField,
  Button,
  Autocomplete,
  IconButton,
  Typography,
} from "@mui/material";
import { IPromotion } from "../interfaces";
import { useGlobalAlert } from "../contexts/alertContext";
import { UserContext } from "../contexts/userContext";
import {
  addDoc,
  collection,
  getDocs,
  query,
  doc,
  updateDoc,
  where,
  orderBy,
} from "firebase/firestore";
import { db } from "../firebase-config";
import {
  BUSINESS_COLLECTION,
  PROMOTIONS_COLLECTION,
  STOCK_COLLECTION,
} from "../utils/constants";
import { LoadingButton } from "@mui/lab";
import GenericButtons from "./GenericButtons";

const initialPromotion: IPromotion = {
  id: "",
  name: "",
  type: "combo",
  isActive: true,
  value: "",
  applies_to: [],
  startDate: new Date(),
  endDate: "",
  createdAt: new Date(),
  updatedAt: new Date(),
};

// Solo tipo combo para este caso

const CreatePromotionModal = ({
  show,
  selectedPromotion,
  handleClose,
  refetch,
}: {
  show: boolean;
  selectedPromotion?: IPromotion;
  handleClose: () => void;
  refetch(): void;
}) => {
  const { businessData } = useContext(UserContext);
  const { showAlert } = useGlobalAlert();
  const [loading, setLoading] = useState(false);
  const [promotion, setPromotion] = useState<IPromotion>(
    selectedPromotion || initialPromotion
  );
  const [product, setProduct] = useState(
    {} as { id: string; name: string; type: string; quantity_needed: number }
  );
  const [isEdit, setIsEdit] = useState(false);
  const [products, setProducts] = useState<
    { id: string; name: string; quantity: number; type: string }[]
  >([]);

  const add = (idx: number) => {
    const updated = promotion.applies_to.map((p, i) =>
      i === idx
        ? {
            ...p,
            quantity_needed: (p.quantity_needed || 1) + 1,
          }
        : p
    );
    setPromotion({ ...promotion, applies_to: updated });
  };

  const subtract = (id: string, idx: number) => {
    const product = promotion.applies_to.find((p) => p.id === id);
    if (product?.quantity_needed === 1) {
      const updated = promotion.applies_to.filter((p) => p.id !== id);
      setPromotion({ ...promotion, applies_to: updated });
      return;
    }
    const updated = promotion.applies_to.map((p, i) =>
      i === idx
        ? {
            ...p,
            quantity_needed: Math.max(1, (p.quantity_needed || 1) - 1),
          }
        : p
    );
    setPromotion({ ...promotion, applies_to: updated });
  };

  // Obtener productos de Firestore al abrir el modal
  useEffect(() => {
    if (!show) return;
    const fetchProducts = async () => {
      const productsRef = collection(
        db,
        BUSINESS_COLLECTION,
        businessData.uid,
        STOCK_COLLECTION
      );
      const { docs } = await getDocs(
        query(productsRef, where("status", "==", "enabled"), orderBy("name"))
      );
      if (docs.length > 0)
        setProducts(
          docs.map((doc) => ({
            id: doc.id,
            name: doc.data().name,
            quantity: doc.data().quantity,
            type: doc.data().type,
          }))
        );
    };
    fetchProducts();
  }, [show, businessData]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setPromotion((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddProduct = () => {
    setPromotion({
      ...promotion,
      applies_to: promotion.applies_to
        ? promotion.applies_to.concat(product)
        : [product],
    });
    setProduct(
      {} as { id: string; name: string; type: string; quantity_needed: number }
    );
  };

  const handleUpdate = async () => {
    setLoading(true);
    const subCollectionRef = collection(
      db,
      BUSINESS_COLLECTION,
      businessData.uid,
      PROMOTIONS_COLLECTION
    );
    const ingredientDoc = doc(subCollectionRef, promotion.id);
    try {
      await updateDoc(ingredientDoc, promotion as any);
      setPromotion(initialPromotion);
      handleClose();
      refetch();
      showAlert("Promoción actualizada exitosamente", "success");
    } catch (error) {
      showAlert("Error al actualizar la promoción", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const promotionsRef = collection(
        db,
        BUSINESS_COLLECTION,
        businessData.uid,
        PROMOTIONS_COLLECTION
      );
      const promoToSave = {
        ...promotion,
        type: "combo",
        isActive: true,
        startDate: new Date(),
        createdAt: new Date(),
        updatedAt: new Date(),
      };
      await addDoc(promotionsRef, promoToSave);
      setPromotion(initialPromotion);
      handleClose();
      refetch();
      showAlert("Promoción creada exitosamente", "success");
    } catch (err) {
      showAlert(
        "Error al guardar la promoción o actualizar productos",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedPromotion && Object.keys(selectedPromotion).length > 0)
      setIsEdit(true);
  }, [selectedPromotion]);

  return (
    <Dialog open={show} onClose={handleClose} maxWidth="sm" fullWidth>
      <IconButton onClick={handleClose} className="close-icon">
        <span className="material-symbols-rounded">close</span>
      </IconButton>
      <DialogTitle>Crear promoción combo</DialogTitle>
      <Grid container spacing={2} sx={{ p: 3 }}>
        <Grid size={12}>
          <TextField
            label="Nombre"
            name="name"
            value={promotion.name}
            onChange={handleChange}
            fullWidth
            size="small"
          />
        </Grid>
        <Grid size={8}>
          <Autocomplete
            options={products}
            getOptionLabel={(option) => option.name || ""}
            value={product as any}
            onChange={(_, newValue, reason: string) => {
              if (reason === "clear")
                setProduct(
                  {} as {
                    id: string;
                    name: string;
                    type: string;
                    quantity_needed: number;
                  }
                );
              if (newValue) {
                setProduct({ ...newValue, quantity_needed: 1 });
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Producto del combo"
                placeholder="Selecciona un producto"
                size="small"
                fullWidth
              />
            )}
            fullWidth
          />
        </Grid>
        <Grid size={4}>
          <Button
            fullWidth
            variant="contained"
            color="secondary"
            disabled={!product.name}
            onClick={handleAddProduct}
          >
            Agregar
          </Button>
        </Grid>

        {/* List of products in applies_to with quantity_needed controls */}
        {promotion.applies_to && promotion.applies_to.length > 0 && (
          <Grid
            container
            spacing={1}
            sx={{ mt: 2 }}
            display="flex"
            alignItems="center"
            width="100%"
          >
            {promotion.applies_to.map((prod, idx) => (
              <React.Fragment key={prod.id}>
                <Grid size={8}>
                  <Typography>{prod.name}</Typography>
                </Grid>
                <GenericButtons
                  add={add}
                  subtract={subtract}
                  id={prod.id}
                  quantity={prod.quantity_needed}
                  index={idx}
                />
              </React.Fragment>
            ))}
          </Grid>
        )}
        <Grid size={12}>
          <TextField
            label="Monto del combo"
            name="value"
            value={promotion.value}
            onChange={handleChange}
            fullWidth
            size="small"
            type="number"
          />
        </Grid>
        {/* <Grid item xs={12} md={6}>
          <TextField
            label="Fecha fin (opcional)"
            name="endDate"
            type="date"
            value={promotion.endDate}
            onChange={handleChange}
            fullWidth
            size="small"
            InputLabelProps={{ shrink: true }}
          />
        </Grid> */}
        <Grid size={12} display="flex" justifyContent="flex-end">
          <Button
            variant="outlined"
            color="secondary"
            onClick={handleClose}
            sx={{ mr: 2 }}
          >
            Cancelar
          </Button>
          <LoadingButton
            loading={loading}
            disabled={
              loading ||
              !promotion.name ||
              !promotion.value ||
              !promotion.applies_to ||
              promotion.applies_to.length === 0
            }
            variant="contained"
            color="secondary"
            onClick={() => (isEdit ? handleUpdate() : handleSubmit())}
          >
            Guardar
          </LoadingButton>
        </Grid>
      </Grid>
    </Dialog>
  );
};

export default CreatePromotionModal;
